import React, { useState, useEffect, useRef } from 'react';
import { Plus, Edit, DollarSign, AlertTriangle, Search, Filter, Users, Hash, Printer, ChevronUp, ChevronDown, Menu, FileText, ChevronDown as ChevronDownIcon, X, ArrowRight, LayoutGrid, Trash2, Download } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { lotesService } from '../../services/lotesService';
import leiloesService from '../../services/leiloesService';
import { ordemEntradaService } from '../../services/ordemEntradaService';
import { useToast } from '../../hooks/useToast';
import { useOrganizador } from '../../contexts/OrganizadorContext';
import { useLabelCurral } from '../../hooks/useLabelCurral';
import { useSidebar } from '../../components/layout/Layout';
import { usePermissao } from '../../hooks/usePermissao';
import FichaLeiloeiroIndividual from '../../components/catalogos/FichaLeiloeiroIndividual';
import ContratoUnicoButton from '../../components/contratos/ContratoUnicoButton';
import ContratoUmaViaButton from '../../components/contratos/ContratoUmaVia';
import NotaPromissoriaButton from '../../components/contratos/NotaPromissoriaButton';
import NotaCompromissoButton from '../../components/contratos/NotaCompromissoButton';
import ContratoNotaButton from '../../components/contratos/ContratoNotaButton';
import Contrato3ViasButton from '../../components/contratos/Contrato3ViasButton';
import FichaLeiloeiroButton from '../../components/contratos/FichaLeiloeiroButton';
import { contratoService } from '../../services/contratoService';
import { meiosPagamentoService } from '../../services/meiosPagamentoService';
import { contem } from '../../utils/texto'

const formatarPercentual = (pct) => Number.isInteger(pct) ? pct : parseFloat(pct.toFixed(1));

const LotesLista = () => {
  const navigate = useNavigate();
  const { organizadorAtivo } = useOrganizador();
  const { setSidebarOpen } = useSidebar();
  const { podeEditar } = usePermissao();
  const labelCurral = useLabelCurral();
  const canEdit = podeEditar('lotes');
  const isFluxoCavalo = organizadorAtivo?.fluxo_leilao === 'comissao_cavalo';
  const rotaNovoLote = (leilaoId) => isFluxoCavalo ? `/lotes/novo-cavalo?leilao=${leilaoId}` : `/lotes/novo?leilao=${leilaoId}`;
  const rotaEditarLote = (loteId) => isFluxoCavalo ? `/lotes/editar-cavalo/${loteId}` : `/lotes/editar/${loteId}`;
  const toast = useToast();
  
  const [lotes, setLotes] = useState([]);
  const [leiloes, setLeiloes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedLeilao, setSelectedLeilao] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [sortField, setSortField] = useState('numero_lote');
  const [sortDirection, setSortDirection] = useState('asc');
  const [totais, setTotais] = useState({
    totalLotes: 0,
    totalAnimais: 0,
    totalFemeas: 0,
    totalMachos: 0,
    totalMistos: 0,
    totalFaturados: 0,
    totalDefesa: 0
  });
  const [fichaLeiloeiroLote, setFichaLeiloeiroLote] = useState(null);
  const [todosLotes, setTodosLotes] = useState([]);
  const [exportModal, setExportModal] = useState(false);
  const [exportLeilaoId, setExportLeilaoId] = useState('');
  const [exportDataInicio, setExportDataInicio] = useState('');
  const [exportDataFim, setExportDataFim] = useState('');
  const [exportFormato, setExportFormato] = useState('xlsx');
  const [exportando, setExportando] = useState(false);

  const [dropdownOpen, setDropdownOpen] = useState(null);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, right: 0 });
  const dropdownRef = useRef(null);
  const [contextMenu, setContextMenu] = useState(null); // { x, y, lote }
  const [contratoConfig, setContratoConfig] = useState(null);
  const [valorVendaModal, setValorVendaModal] = useState(null); // lote selecionado
  const [valorVendaInput, setValorVendaInput] = useState('');
  const [valorVendaExcecaoPorCabeca, setValorVendaExcecaoPorCabeca] = useState(false);
  const [salvandoValorVenda, setSalvandoValorVenda] = useState(false);
  const [meiosPagamento, setMeiosPagamento] = useState([]);
  const [showConfirmDeleteLote, setShowConfirmDeleteLote] = useState(false);
  const [loteToDelete, setLoteToDelete] = useState(null);
  const [itemsPerPage, setItemsPerPage] = useState(
    () => parseInt(localStorage.getItem('rematei-lotes-perpage') || '15')
  );

  useEffect(() => {
    if (organizadorAtivo?.id) {
      carregarLeiloes();
      carregarContratoConfig();
      meiosPagamentoService.listar(organizadorAtivo.id)
        .then(meios => setMeiosPagamento(meios || []))
        .catch(() => setMeiosPagamento([]));
    }
  }, [organizadorAtivo]);

  useEffect(() => {
    if (selectedLeilao) {
      carregarLotes(search, statusFilter);
    }
  }, [selectedLeilao]);

  // Leilão com filtro automático: mostra a posição de cada lote na ordem de entrada
  const [posicoesOrdem, setPosicoesOrdem] = useState({});
  useEffect(() => {
    setPosicoesOrdem({});
    if (!selectedLeilao) return;
    let cancelado = false;
    ordemEntradaService.obterConfiguracao(selectedLeilao)
      .then(async cfg => {
        if (!cfg?.filtro_bateria_id) return;
        const lista = await ordemEntradaService.listarOrdem(selectedLeilao);
        if (cancelado) return;
        const mapa = {};
        lista.forEach(o => { mapa[o.lote_id] = o.posicao; });
        setPosicoesOrdem(mapa);
      })
      .catch(() => {});
    return () => { cancelado = true; };
  }, [selectedLeilao]);

  useEffect(() => {
    if (todosLotes.length > 0) {
      aplicarFiltros(todosLotes, 1, search, statusFilter);
    }
  }, [search, statusFilter]);

  useEffect(() => {
    if (todosLotes.length > 0) {
      aplicarFiltros(todosLotes, currentPage, search, statusFilter);
    }
  }, [currentPage, sortField, sortDirection]);

  useEffect(() => {
    if (todosLotes.length > 0) {
      aplicarFiltros(todosLotes, 1, search, statusFilter);
    }
  }, [itemsPerPage]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(null);
      }
      setContextMenu(null);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const carregarLeiloes = async () => {
    try {
      const data = await leiloesService.getByOrganizador(organizadorAtivo.id);
      setLeiloes(data || []);
      if (data && data.length > 0) {
        const ativo = data.find(l => l.status === 'on') || data[0];
        setSelectedLeilao(ativo.id.toString());
      } else {
        setLoading(false);
      }
    } catch (error) {
      console.error('Erro ao carregar leilões:', error);
      toast.error('Erro ao carregar leilões');
      setLoading(false);
    }
  };

  const handleExportar = async () => {
    setExportando(true);
    try {
      const blob = await lotesService.exportarVendas(organizadorAtivo.id, {
        leilaoId: exportLeilaoId || undefined,
        dataInicio: exportDataInicio || undefined,
        dataFim: exportDataFim || undefined,
        formato: exportFormato,
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = exportFormato === 'csv' ? 'vendas_csv.zip' : 'vendas.xlsx';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      setExportModal(false);
      toast.success('Exportação gerada com sucesso!');
    } catch (error) {
      console.error('Erro ao exportar vendas:', error);
      toast.error('Erro ao gerar exportação');
    } finally {
      setExportando(false);
    }
  };

  const carregarContratoConfig = async () => {
    try {
      const config = await contratoService.getConfig(organizadorAtivo.id);
      setContratoConfig(config);
    } catch (error) {
      console.error('Erro ao carregar config de contrato:', error);
    }
  };

  const handleExcluirLote = (lote) => {
    setLoteToDelete(lote);
    setShowConfirmDeleteLote(true);
  };

  const handleSalvarValorVenda = async () => {
    if (!valorVendaInput || parseFloat(valorVendaInput) <= 0) return;
    const lote = valorVendaModal;
    const leilaoAtual = leiloes.find(l => l.id.toString() === selectedLeilao);
    const isPorParcela = leilaoAtual?.regra_faturamento === 'por_parcela' && !valorVendaExcecaoPorCabeca;
    const valorUnit = parseFloat(valorVendaInput);
    let valorTotal = valorUnit * (lote.qtd || 1);
    if (isPorParcela && leilaoAtual?.meio_pagamento_padrao_id) {
      const meio = meiosPagamento.find(m => m.id === leilaoAtual.meio_pagamento_padrao_id);
      const qtdParcelas = meio?.grupos_parcelas?.reduce((acc, g) => acc + g.quantidade * (g.peso || 1), 0) || 1;
      valorTotal = valorUnit * qtdParcelas * (lote.qtd || 1);
    }
    try {
      setSalvandoValorVenda(true);
      await lotesService.salvarValorVenda(organizadorAtivo.id, lote.id, valorUnit, valorTotal);
      toast.success(`Valor de venda salvo — lote ${lote.numero_lote} está EDITADO`);
      setValorVendaModal(null);
      setValorVendaExcecaoPorCabeca(false);
      await carregarLotes(search, statusFilter);
    } catch (err) {
      console.error('Erro ao salvar valor de venda:', err);
      toast.error(err.response?.data?.detail || 'Erro ao salvar valor de venda');
    } finally {
      setSalvandoValorVenda(false);
    }
  };

  const confirmarExclusaoLote = async () => {
    if (!loteToDelete) return;
    try {
      await lotesService.excluirLote(organizadorAtivo.id, loteToDelete.id);
      toast.success(`Lote ${loteToDelete.numero_lote} excluído com sucesso!`);
      await carregarLotes(search, statusFilter);
    } catch (err) {
      console.error('Erro ao excluir lote:', err);
      if (err.response?.status === 400) {
        toast.error(err.response.data?.detail || 'Somente lotes com status NOVO podem ser excluídos');
      } else {
        toast.error('Erro ao excluir lote');
      }
    }
    setLoteToDelete(null);
    setShowConfirmDeleteLote(false);
  };

  const handleSort = (field) => {
    const newDirection = sortField === field && sortDirection === 'asc' ? 'desc' : 'asc';
    setSortField(field);
    setSortDirection(newDirection);
  };

  const getSortIcon = (field) => {
    if (sortField !== field) return null;
    return sortDirection === 'asc' ? 
      <ChevronUp size={16} className="text-blue-600" /> : 
      <ChevronDown size={16} className="text-blue-600" />;
  };

  const aplicarFiltros = (data, page, searchTerm, status) => {
    let filteredData = data;

    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      filteredData = filteredData.filter(lote =>
        contem(lote.numero_lote, q) ||
        contem(lote.tipo_animal?.nome, q) ||
        contem(lote.vendedor?.nome, q) ||
        contem(lote.vendedor?.apelido, q) ||
        contem(lote.fazenda?.nome, q) ||
        contem(lote.fazenda_ficticia, q) ||
        contem(lote.comprador?.nome, q) ||
        contem(lote.comprador?.apelido, q) ||
        contem(lote.comprador?.fazenda, q) ||
        contem(lote.curral, q) ||
        contem(lote.raca?.nome, q)
      );
    }

    if (status) {
      filteredData = filteredData.filter(lote => lote.status === status);
    }

    const sortNumeroLote = (n) => {
      const m = String(n || '').match(/^(\d+)(.*)/);
      return m ? [parseInt(m[1], 10), m[2].toLowerCase()] : [999999, String(n || '').toLowerCase()];
    };

    filteredData = [...filteredData].sort((a, b) => {
      if (sortField === 'numero_lote') {
        const [an, as_] = sortNumeroLote(a.numero_lote);
        const [bn, bs] = sortNumeroLote(b.numero_lote);
        const cmp = an !== bn ? an - bn : as_.localeCompare(bs);
        return sortDirection === 'asc' ? cmp : -cmp;
      }
      let aValue, bValue;
      switch (sortField) {
        case 'tipo_animal': aValue = a.tipo_animal?.nome || ''; bValue = b.tipo_animal?.nome || ''; break;
        case 'vendedor': aValue = a.vendedor?.nome || ''; bValue = b.vendedor?.nome || ''; break;
        case 'comprador': aValue = a.comprador?.nome || ''; bValue = b.comprador?.nome || ''; break;
        case 'valor_venda': aValue = a.valor_venda_total || a.valor_venda_unidade || 0; bValue = b.valor_venda_total || b.valor_venda_unidade || 0; break;
        case 'status': aValue = a.status || ''; bValue = b.status || ''; break;
        default: return 0;
      }
      if (typeof aValue === 'string') { aValue = aValue.toLowerCase(); bValue = bValue.toLowerCase(); }
      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    // Totais do leilão inteiro: não mudam com a busca/filtro de status
    setTotais({
      totalLotes: data.length,
      totalAnimais: data.reduce((sum, l) => sum + (l.qtd || 0), 0),
      totalFemeas: data.filter(l => l.sexo === 'Fêmea').reduce((sum, l) => sum + (l.qtd || 0), 0),
      totalMachos: data.filter(l => l.sexo === 'Macho').reduce((sum, l) => sum + (l.qtd || 0), 0),
      totalMistos: data.filter(l => l.sexo === 'Misto').reduce((sum, l) => sum + (l.qtd || 0), 0),
      totalFaturados: data.filter(l => l.status === 'FATURADO').length,
      totalDefesa: data.filter(l => l.status === 'DEFESA').length,
    });

    const totalPgs = Math.ceil(filteredData.length / itemsPerPage);
    const safePage = Math.min(page, totalPgs || 1);
    const startIndex = (safePage - 1) * itemsPerPage;
    setLotes(filteredData.slice(startIndex, startIndex + itemsPerPage));
    setTotalPages(totalPgs);
    setCurrentPage(safePage);
  };

  const carregarLotes = async (searchTerm = '', status = '') => {
    if (!selectedLeilao) return;
    try {
      setLoading(true);
      const data = await lotesService.listarLotesPorLeilao(organizadorAtivo.id, selectedLeilao);
      setTodosLotes(data || []);
      aplicarFiltros(data || [], 1, searchTerm, status);
      localStorage.setItem('rematei-lote-updated', Date.now().toString());
    } catch (error) {
      console.error('Erro ao carregar lotes:', error);
      setLotes([]);
      toast.error('Erro ao carregar lotes');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'NOVO': return 'bg-blue-100 text-blue-800';
      case 'FATURADO': return 'bg-green-100 text-green-800';
      case 'DEFESA': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const formatCurrency = (value) => {
    if (!value) return '-';
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  if (loading && !selectedLeilao) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!loading && leiloes.length === 0) {
    return (
      <div className="flex flex-col justify-center items-center h-64 gap-4 text-center">
        <div className="text-gray-400 text-5xl">🐄</div>
        <div>
          <p className="text-gray-600 font-semibold text-lg">Nenhum leilão cadastrado</p>
          <p className="text-gray-400 text-sm mt-1">Crie um leilão primeiro para poder gerenciar os lotes.</p>
        </div>
        <button
          onClick={() => navigate('/leiloes/novo')}
          className="mt-2 px-5 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
        >
          Criar primeiro leilão
        </button>
      </div>
    );
  }

  return (
    <div className="">
      <div className="space-y-5 max-w-[1400px] mx-auto">
        {/* Header com botão hambúrguer para mobile */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-xl px-4 sm:px-6 py-4 sm:py-6">
          <div className="flex items-center justify-between mb-4 md:hidden">
            <button
              onClick={() => setSidebarOpen(true)}
              className="p-2 rounded-xl text-white hover:bg-white/20 transition-all duration-200"
            >
              <Menu className="h-6 w-6" />
            </button>
            <h1 className="text-lg font-semibold text-white truncate">Lotes</h1>
            <div className="w-10"></div>
          </div>
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 sm:gap-4">
            <div className="min-w-0 flex-1">
              <h1 className="text-xl sm:text-3xl font-bold text-white hidden md:block">{isFluxoCavalo ? 'Lotes' : 'Lotes de Gado'}</h1>
              <p className="text-blue-100 mt-1 text-sm sm:text-base hidden sm:block">Gerencie os lotes dos seus leilões</p>
            </div>
            <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
              {(organizadorAtivo?.produtos_premium || []).includes('exportacao_vendas') && (
                <button
                  onClick={() => { setExportLeilaoId(selectedLeilao || ''); setExportModal(true); }}
                  className="w-full sm:w-auto bg-white/10 hover:bg-white/20 text-white border border-white/30 px-4 sm:px-5 py-3 rounded-xl flex items-center justify-center gap-2 transition-all font-medium text-sm sm:text-base"
                >
                  <Download size={18} />
                  Exportar
                </button>
              )}
              {!isFluxoCavalo && (
                <button
                  onClick={() => window.open(`/mapa-curral?leilao=${selectedLeilao}`, '_blank')}
                  className="w-full sm:w-auto bg-white/10 hover:bg-white/20 text-white border border-white/30 px-4 sm:px-5 py-3 rounded-xl flex items-center justify-center gap-2 transition-all disabled:opacity-50 font-medium text-sm sm:text-base"
                  disabled={!selectedLeilao}
                >
                  <LayoutGrid size={18} />
                  Mapa de {labelCurral}
                </button>
              )}
              {canEdit && (
                <button
                  onClick={() => navigate(rotaNovoLote(selectedLeilao))}
                  className="w-full sm:w-auto bg-white hover:bg-gray-100 text-blue-600 px-4 sm:px-6 py-3 rounded-xl flex items-center justify-center gap-2 transition-all shadow-lg hover:shadow-xl disabled:opacity-50 font-medium text-sm sm:text-base"
                  disabled={!selectedLeilao}
                >
                  <Plus size={18} />
                  Novo Lote
                </button>
              )}
            </div>
          </div>
        </div>

      {/* Mobile Action Button */}
      {canEdit && (
        <div className="md:hidden fixed bottom-6 right-6 z-40">
          <button
            onClick={() => navigate(rotaNovoLote(selectedLeilao))}
            className="bg-blue-600 hover:bg-blue-700 text-white p-4 rounded-full shadow-lg hover:shadow-xl disabled:opacity-50 transition-all"
            disabled={!selectedLeilao}
            title="Novo Lote"
          >
            <Plus size={24} />
          </button>
        </div>
      )}

        {/* Stats Cards */}
        <div className={`grid grid-cols-2 sm:grid-cols-4 ${isFluxoCavalo ? 'lg:grid-cols-4' : 'lg:grid-cols-7'} gap-2 mb-4`}>
          {(isFluxoCavalo ? [
            { label: 'Lotes',     value: totais.totalLotes,     icon: Hash,          bg: 'bg-blue-100',   color: 'text-blue-600'   },
            { label: 'Animais',   value: totais.totalAnimais,   icon: Users,         bg: 'bg-green-100',  color: 'text-green-600'  },
            { label: 'Faturados', value: totais.totalFaturados, icon: DollarSign,    bg: 'bg-yellow-100', color: 'text-yellow-600' },
            { label: 'Defesa',    value: totais.totalDefesa,    icon: AlertTriangle, bg: 'bg-red-100',    color: 'text-red-600'    },
          ] : [
            { label: 'Lotes',     value: totais.totalLotes,     icon: Hash,          bg: 'bg-blue-100',   color: 'text-blue-600'   },
            { label: 'Animais',   value: totais.totalAnimais,   icon: Users,         bg: 'bg-green-100',  color: 'text-green-600'  },
            { label: 'Fêmeas',    value: totais.totalFemeas,    icon: Users,         bg: 'bg-pink-100',   color: 'text-pink-600'   },
            { label: 'Machos',    value: totais.totalMachos,    icon: Users,         bg: 'bg-blue-100',   color: 'text-blue-600'   },
            { label: 'Mistos',    value: totais.totalMistos,    icon: Users,         bg: 'bg-purple-100', color: 'text-purple-600' },
            { label: 'Faturados', value: totais.totalFaturados, icon: DollarSign,    bg: 'bg-yellow-100', color: 'text-yellow-600' },
            { label: 'Defesa',    value: totais.totalDefesa,    icon: AlertTriangle, bg: 'bg-red-100',    color: 'text-red-600'    },
          ]).map(({ label, value, icon: Icon, bg, color }) => (
            <div key={label} className="bg-white rounded-xl p-2.5 shadow-sm border border-gray-100">
              <div className="flex items-center gap-2">
                <div className={`p-1.5 ${bg} rounded-lg flex-shrink-0`}>
                  <Icon size={13} className={color} />
                </div>
                <div className="min-w-0">
                  <p className="text-xs text-gray-500 truncate">{label}</p>
                  <p className="text-base font-bold text-gray-900">{value}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Filtros */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-3 sm:p-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Leilão</label>
              <select
                value={selectedLeilao}
                onChange={(e) => setSelectedLeilao(e.target.value)}
                className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm sm:text-base"
              >
                <option value="">Selecione um leilão...</option>
                {leiloes.map(leilao => (
                  <option key={leilao.id} value={leilao.id}>
                    {leilao.nome} - {new Date(leilao.data_leilao).toLocaleDateString('pt-BR')}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Buscar</label>
              <div className="relative">
                <Search size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder={`Nº lote, vendedor, comprador, fazenda, raça, ${labelCurral.toLowerCase()}...`}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-full pl-9 sm:pl-10 pr-3 sm:pr-4 py-2.5 sm:py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm sm:text-base"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm sm:text-base"
              >
                <option value="">Todos</option>
                <option value="NOVO">Novo</option>
                <option value="FATURADO">Faturado</option>
                <option value="DEFESA">Defesa</option>
                <option value="EDITADO">Editado</option>
              </select>
            </div>
          </div>
        </div>

        {/* Tabela */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100">
          {loading ? (
            <div className="p-4">
              <div className="flex items-center gap-3 mb-4 px-2">
                <div className="animate-spin rounded-full h-5 w-5 border-2 border-blue-600 border-t-transparent flex-shrink-0" />
                <span className="text-sm text-gray-500">Carregando lotes...</span>
              </div>
              <div className="divide-y divide-gray-100">
                {Array.from({ length: 8 }).map((_, i) => (
                  <div key={i} className="flex items-center gap-3 py-3 px-2">
                    <div className="w-9 h-9 rounded-lg bg-gray-200 animate-pulse flex-shrink-0" />
                    <div className="hidden sm:flex flex-col gap-1.5 w-36 flex-shrink-0">
                      <div className="h-3.5 bg-gray-200 rounded animate-pulse w-24" />
                      <div className="h-3 bg-gray-100 rounded animate-pulse w-32" />
                    </div>
                    <div className="flex flex-col gap-1.5 flex-1 min-w-0">
                      <div className="h-3.5 bg-gray-200 rounded animate-pulse w-40" />
                      <div className="h-3 bg-gray-100 rounded animate-pulse w-28" />
                    </div>
                    <div className="hidden md:flex flex-col gap-1.5 flex-1 min-w-0">
                      <div className="h-3.5 bg-gray-200 rounded animate-pulse w-32" />
                    </div>
                    <div className="hidden lg:block w-24 flex-shrink-0">
                      <div className="h-3.5 bg-gray-200 rounded animate-pulse w-20" />
                    </div>
                    <div className="w-16 flex-shrink-0">
                      <div className="h-6 bg-gray-200 rounded-md animate-pulse w-16" />
                    </div>
                    <div className="w-24 flex-shrink-0 flex gap-1 justify-center">
                      <div className="h-7 w-7 bg-gray-100 rounded-md animate-pulse" />
                      <div className="h-7 w-14 bg-gray-100 rounded-md animate-pulse" />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : selectedLeilao ? (
            lotes.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="min-w-full">
                <thead>
                  <tr className="border-b border-gray-200 bg-gray-50">
                    <th style={{width:'56px'}} className="pl-4 pr-2 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">#</th>
                    <th style={{width:'210px'}} className="px-3 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider hidden sm:table-cell cursor-pointer select-none" onClick={() => handleSort('tipo_animal')}>
                      <div className="flex items-center gap-1">Animais {getSortIcon('tipo_animal')}</div>
                    </th>
                    <th style={{width:'25%'}} className="px-3 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer select-none" onClick={() => handleSort('vendedor')}>
                      <div className="flex items-center gap-1">Vendedor {getSortIcon('vendedor')}</div>
                    </th>
                    <th style={{width:'25%'}} className="px-3 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider hidden md:table-cell cursor-pointer select-none" onClick={() => handleSort('comprador')}>
                      <div className="flex items-center gap-1">Comprador {getSortIcon('comprador')}</div>
                    </th>
                    <th style={{width:'130px'}} className="px-3 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider hidden lg:table-cell cursor-pointer select-none" onClick={() => handleSort('valor_venda')}>
                      <div className="flex items-center gap-1">Valor {getSortIcon('valor_venda')}</div>
                    </th>
                    <th style={{width:'96px'}} className="px-3 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer select-none" onClick={() => handleSort('status')}>
                      <div className="flex items-center gap-1">Status {getSortIcon('status')}</div>
                    </th>
                    <th style={{width:'112px'}} className="pl-2 pr-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">Ações</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {lotes.map((lote) => (
                    <tr
                      key={lote.id}
                      className={`group transition-colors ${lote.status === 'FATURADO' ? 'bg-green-50 hover:bg-green-100/60' : lote.status === 'DEFESA' ? 'bg-red-50 hover:bg-red-100/60' : 'bg-white hover:bg-blue-50/40'} ${canEdit ? 'cursor-pointer' : ''}`}
                      onClick={() => canEdit && navigate(rotaEditarLote(lote.id))}
                      onMouseDown={(e) => {
                        if (e.button === 1 && canEdit) { e.preventDefault(); window.open(rotaEditarLote(lote.id), '_blank'); }
                      }}
                      onContextMenu={(e) => {
                        e.preventDefault();
                        setContextMenu({ x: e.clientX, y: e.clientY, lote });
                      }}
                    >
                      {/* Lote */}
                      <td className="pl-4 pr-2 py-3">
                        <a
                          href={rotaEditarLote(lote.id)}
                          onClick={(e) => e.preventDefault()}
                          className={`inline-flex items-center justify-center w-9 h-9 rounded-lg font-bold text-xs transition-colors border ${
                            lote.status === 'FATURADO'
                              ? 'bg-green-100 group-hover:bg-green-200 border-green-300 text-green-800'
                              : lote.status === 'DEFESA'
                              ? 'bg-red-100 group-hover:bg-red-200 border-red-300 text-red-800'
                              : 'bg-blue-50 group-hover:bg-blue-100 border-blue-200 text-blue-700'
                          }`}
                        >
                          {lote.numero_lote}
                        </a>
                        {posicoesOrdem[lote.id] != null && (
                          <div className="mt-0.5 -mx-2 w-[3.25rem] text-center whitespace-nowrap text-[10px] leading-none font-semibold text-purple-600" title="Ordem de entrada (OD)">
                            OD: {posicoesOrdem[lote.id]}º
                          </div>
                        )}
                      </td>

                      {/* Animais */}
                      <td className="px-3 py-3 hidden sm:table-cell">
                        {isFluxoCavalo ? (
                          lote.animais && lote.animais.length > 0 ? (
                            <div className="space-y-0.5">
                              {lote.animais.map(a => {
                                const pct = parseFloat(a.percentual_venda ?? 100)
                                const parcial = Math.abs(pct - 100) > 0.01
                                return (
                                  <p key={a.id} className="text-sm font-semibold text-gray-800 leading-tight truncate max-w-[190px]">
                                    {parcial && <span className="text-amber-600 font-bold">{formatarPercentual(pct)}% </span>}
                                    {a.nome}
                                  </p>
                                )
                              })}
                            </div>
                          ) : (
                            <p className="text-sm font-semibold text-gray-800 leading-tight">
                              {lote.qtd || 0} animal{lote.qtd === 1 ? '' : 'is'}
                            </p>
                          )
                        ) : (
                          <>
                            <p className="text-sm font-semibold text-gray-800 leading-tight">{lote.qtd} {lote.sexo}</p>
                            <p className="text-xs text-gray-600 mt-0.5 leading-tight line-clamp-1">
                              {[lote.tipo_animal?.nome, lote.raca?.nome, lote.idade?.nome].filter(Boolean).join(' · ')}
                            </p>
                          </>
                        )}
                      </td>

                      {/* Vendedor */}
                      <td className="px-3 py-3 max-w-0 overflow-hidden">
                        <p className="text-sm font-medium text-gray-800 truncate leading-tight">
                          {lote.vendedor?.nome || <span className="text-gray-400 italic">—</span>}
                          {lote.multi_vendedores?.length > 1 && (
                            <span className="ml-1 text-xs font-semibold text-amber-600">+{lote.multi_vendedores.length - 1}</span>
                          )}
                        </p>
                        {lote.multi_vendedores?.length > 1 && (
                          <p className="text-xs text-amber-700 truncate leading-tight" title={lote.multi_vendedores.map(v => `${v.nome} (${v.percentual}%)`).join(' + ')}>
                            {lote.multi_vendedores.map(v => `${v.nome.split(' ')[0]} ${v.percentual}%`).join(' + ')}
                          </p>
                        )}
                        {(lote.fazenda?.nome || lote.fazenda_ficticia) && (
                          <p className="text-xs text-gray-600 truncate mt-0.5 leading-tight hidden sm:block">
                            {lote.fazenda?.nome || lote.fazenda_ficticia}
                          </p>
                        )}
                        {/* Info mobile: animais abaixo do vendedor em telas pequenas */}
                        <p className="text-xs text-gray-600 mt-0.5 sm:hidden truncate">
                          {isFluxoCavalo
                            ? (lote.animais && lote.animais.length > 0
                                ? lote.animais.map(a => {
                                    const pct = parseFloat(a.percentual_venda ?? 100)
                                    const parcial = Math.abs(pct - 100) > 0.01
                                    return parcial ? `${formatarPercentual(pct)}% ${a.nome}` : a.nome
                                  }).join(', ')
                                : `${lote.qtd || 0} animal${lote.qtd === 1 ? '' : 'is'}`)
                            : `${lote.qtd} ${lote.sexo} · ${lote.tipo_animal?.nome || ''}`}
                        </p>
                      </td>

                      {/* Comprador */}
                      <td className="px-3 py-3 hidden md:table-cell max-w-0 overflow-hidden">
                        {lote.comprador ? (
                          <>
                            <p className="text-sm font-medium text-gray-800 truncate leading-tight">
                              {lote.comprador.nome}
                              {lote.multi_compradores?.length > 1 && (
                                <span className="ml-1 text-xs font-semibold text-amber-600">+{lote.multi_compradores.length - 1}</span>
                              )}
                            </p>
                            {lote.multi_compradores?.length > 1 && (
                              <p className="text-xs text-amber-700 truncate leading-tight" title={lote.multi_compradores.map(c => `${c.nome} (${c.percentual}%)`).join(' + ')}>
                                {lote.multi_compradores.map(c => `${c.nome.split(' ')[0]} ${c.percentual}%`).join(' + ')}
                              </p>
                            )}
                          </>
                        ) : (
                          <span className="text-gray-300 text-sm">—</span>
                        )}
                      </td>

                      {/* Valor */}
                      <td className="px-3 py-3 hidden lg:table-cell">
                        {(lote.valor_venda_total || lote.valor_venda_unidade) ? (
                          <>
                            <p className="text-sm font-semibold text-green-600 leading-tight whitespace-nowrap">
                              {formatCurrency(lote.valor_venda_total ? lote.valor_venda_total / lote.qtd : lote.valor_venda_unidade)}
                            </p>
                            <p className="text-xs text-gray-600 mt-0.5 leading-tight whitespace-nowrap">
                              Total: {formatCurrency(lote.valor_venda_total || lote.valor_total)}
                            </p>
                          </>
                        ) : (
                          <span className="text-gray-300 text-sm">—</span>
                        )}
                      </td>

                      {/* Status */}
                      <td className="px-3 py-3">
                        <span className={`inline-flex items-center px-2.5 py-1 rounded-md text-xs font-semibold whitespace-nowrap ${getStatusColor(lote.status)}`}>
                          {lote.status}
                        </span>
                      </td>

                      {/* Ações */}
                      <td className="pl-2 pr-4 py-3" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-center gap-1">
                          {canEdit && (
                            <button
                              onClick={() => navigate(rotaEditarLote(lote.id))}
                              className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-all"
                              title="Editar"
                            >
                              <Edit size={14} />
                            </button>
                          )}

                          {canEdit && (lote.status === 'NOVO' || lote.status === 'EDITADO') && (
                            <button
                              onClick={() => { setValorVendaModal(lote); setValorVendaInput(lote.valor_venda_unidade ? String(lote.valor_venda_unidade) : ''); }}
                              className="flex items-center gap-1 px-2 py-1 bg-green-600 hover:bg-green-700 text-white rounded-md transition-all font-semibold text-xs"
                              title="Definir Valor de Venda"
                            >
                              <DollarSign size={12} />
                              Venda
                            </button>
                          )}

                          <div className="relative" ref={dropdownOpen === lote.id ? dropdownRef : null}>
                            <button
                              onClick={(e) => {
                                if (dropdownOpen === lote.id) {
                                  setDropdownOpen(null);
                                } else {
                                  const rect = e.currentTarget.getBoundingClientRect();
                                  const spaceBelow = window.innerHeight - rect.bottom;
                                  const openUp = spaceBelow < 320;
                                  setDropdownPos({
                                    top: openUp ? rect.top - 4 : rect.bottom + 4,
                                    right: window.innerWidth - rect.right,
                                    openUp,
                                  });
                                  setDropdownOpen(lote.id);
                                }
                              }}
                              className="p-1.5 text-orange-500 hover:text-orange-700 hover:bg-orange-50 rounded-md transition-all flex items-center gap-0.5"
                              title="Documentos"
                            >
                              <FileText size={14} />
                              <ChevronDownIcon size={11} />
                            </button>

                            {dropdownOpen === lote.id && (
                              <div className="fixed w-56 bg-white rounded-lg shadow-xl border border-gray-200 z-[9999] overflow-y-auto max-h-[90vh]" style={{ ...(dropdownPos.openUp ? { bottom: window.innerHeight - dropdownPos.top, right: dropdownPos.right } : { top: dropdownPos.top, right: dropdownPos.right }) }}>
                                <div className="py-1 whitespace-nowrap">
                                  <div className="px-3 py-1">
                                    <FichaLeiloeiroButton lote={lote} organizador={organizadorAtivo} dropdown={true} />
                                  </div>
                                  {isFluxoCavalo ? (
                                    <>
                                      {/* Fluxo cavalo: Ficha -> Notas -> Contratos, com destaque no
                                          Contrato 1 Via (já traz nota promissória + instrumento), no
                                          Contrato + Nota e no Contrato 3 Vias. */}
                                      {lote.status === 'FATURADO' && (
                                        <>
                                          <div className="px-3 py-1">
                                            <NotaPromissoriaButton lote={lote} leilao={leiloes.find(l => l.id.toString() === selectedLeilao)} organizador={organizadorAtivo} dropdown={true} />
                                          </div>
                                          <div className="px-3 py-1">
                                            <NotaCompromissoButton lote={lote} organizador={organizadorAtivo} dropdown={true} />
                                          </div>
                                        </>
                                      )}
                                      {(lote.status === 'FATURADO' || lote.status === 'DEFESA') && (
                                        <div className="px-3 py-1">
                                          <ContratoUmaViaButton lote={lote} leilao={leiloes.find(l => l.id.toString() === selectedLeilao)} organizador={organizadorAtivo} />
                                        </div>
                                      )}
                                      {lote.status === 'FATURADO' && (
                                        <>
                                          <div className="px-3 py-1">
                                            <ContratoNotaButton lote={lote} organizador={organizadorAtivo} />
                                          </div>
                                          <div className="px-3 py-1">
                                            <Contrato3ViasButton lote={lote} organizador={organizadorAtivo} />
                                          </div>
                                        </>
                                      )}
                                    </>
                                  ) : (
                                    <>
                                      {(lote.status === 'FATURADO' || lote.status === 'DEFESA') && (
                                        <>
                                          <div className="px-3 py-1">
                                            <ContratoUnicoButton lote={lote} organizador={organizadorAtivo} />
                                          </div>
                                          <div className="px-3 py-1">
                                            <ContratoUmaViaButton lote={lote} leilao={leiloes.find(l => l.id.toString() === selectedLeilao)} organizador={organizadorAtivo} />
                                          </div>
                                        </>
                                      )}
                                      {lote.status === 'FATURADO' && (
                                        <>
                                          <div className="px-3 py-1">
                                            <NotaPromissoriaButton lote={lote} leilao={leiloes.find(l => l.id.toString() === selectedLeilao)} organizador={organizadorAtivo} dropdown={true} />
                                          </div>
                                          <div className="px-3 py-1">
                                            <NotaCompromissoButton lote={lote} organizador={organizadorAtivo} dropdown={true} />
                                          </div>
                                          <div className="px-3 py-1">
                                            <ContratoNotaButton lote={lote} organizador={organizadorAtivo} dropdown={true} />
                                          </div>
                                          <div className="px-3 py-1">
                                            <Contrato3ViasButton lote={lote} organizador={organizadorAtivo} />
                                          </div>
                                        </>
                                      )}
                                    </>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>

                          {canEdit && lote.status === 'FATURADO' && (
                            <button
                              onClick={() => toast.info('Marcar como defesa em desenvolvimento')}
                              className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-md transition-all"
                              title="Marcar como Defesa"
                            >
                              <AlertTriangle size={14} />
                            </button>
                          )}

                          {canEdit && lote.status === 'NOVO' && (
                            <button
                              onClick={() => handleExcluirLote(lote)}
                              className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-md transition-all"
                              title="Excluir Lote"
                            >
                              <Trash2 size={14} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-16">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Hash size={32} className="text-gray-400" />
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">Nenhum lote encontrado</h3>
                <p className="text-gray-500 mb-6 max-w-md mx-auto">
                  {search || statusFilter 
                    ? 'Tente ajustar os filtros de busca para encontrar os lotes desejados'
                    : 'Cadastre o primeiro lote para este leilão e comece a gerenciar seus animais'
                  }
                </p>
                {canEdit && (
                  <button
                    onClick={() => navigate(rotaNovoLote(selectedLeilao))}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-xl transition-all shadow-lg hover:shadow-xl font-medium"
                  >
                    <Plus size={20} className="inline mr-2" />
                    Cadastrar Primeiro Lote
                  </button>
                )}
              </div>
            )
          ) : (
            <div className="text-center py-16">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Filter size={32} className="text-blue-600" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Selecione um leilão</h3>
              <p className="text-gray-500 max-w-md mx-auto">
                Escolha um leilão de gado de corte para visualizar e gerenciar os lotes
              </p>
            </div>
          )}
        </div>

        {/* Pagination */}
        {!loading && todosLotes.length > 0 && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-3 sm:p-4 flex flex-col sm:flex-row justify-between items-center gap-3">
            {/* Seletor de itens por página */}
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <span>Mostrar</span>
              <select
                value={itemsPerPage}
                onChange={(e) => {
                  const val = parseInt(e.target.value);
                  setItemsPerPage(val);
                  localStorage.setItem('rematei-lotes-perpage', String(val));
                }}
                className="border border-gray-300 rounded-lg px-2 py-1.5 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                {[10, 15, 25, 50, 100].map(n => (
                  <option key={n} value={n}>{n}</option>
                ))}
              </select>
              <span>registros por página</span>
            </div>

            {/* Navegação de páginas */}
            {totalPages > 1 && (
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-500 hidden sm:inline">
                  Página {currentPage} de {totalPages}
                </span>
                <div className="flex gap-1">
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                    disabled={currentPage === 1}
                    className="px-3 py-1.5 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors font-medium text-sm whitespace-nowrap"
                  >
                    Anterior
                  </button>
                  {Array.from({ length: Math.min(3, totalPages) }, (_, i) => {
                    let page;
                    if (totalPages <= 3) page = i + 1;
                    else if (currentPage <= 2) page = i + 1;
                    else if (currentPage >= totalPages - 1) page = totalPages - 2 + i;
                    else page = currentPage - 1 + i;
                    return (
                      <button
                        key={page}
                        onClick={() => setCurrentPage(page)}
                        className={`px-3 py-1.5 rounded-lg font-medium transition-colors text-sm ${
                          currentPage === page
                            ? 'bg-blue-600 text-white shadow-sm'
                            : 'border border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        {page}
                      </button>
                    );
                  })}
                  <button
                    onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                    disabled={currentPage === totalPages}
                    className="px-3 py-1.5 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors font-medium text-sm whitespace-nowrap"
                  >
                    Próxima
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Context menu de linha */}
      {contextMenu && (
        <div
          className="fixed z-[9999] bg-white rounded-xl shadow-2xl border border-gray-100 py-1.5 min-w-[200px] overflow-hidden"
          style={{ left: contextMenu.x, top: contextMenu.y }}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <div className="px-3 py-1.5 border-b border-gray-100 mb-1">
            <span className="text-xs font-semibold text-gray-400 uppercase tracking-wide">
              Lote {contextMenu.lote.numero_lote}
            </span>
          </div>
          {canEdit && (
            <button
              onClick={() => { setContextMenu(null); navigate(rotaEditarLote(contextMenu.lote.id)); }}
              className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-700 transition-colors"
            >
              <Edit size={14} />
              Editar lote
            </button>
          )}
          <button
            onClick={() => { setContextMenu(null); window.open(rotaEditarLote(contextMenu.lote.id), '_blank'); }}
            className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-700 transition-colors"
          >
            <ArrowRight size={14} />
            Abrir em nova guia
          </button>
          {canEdit && (contextMenu.lote.status === 'NOVO' || contextMenu.lote.status === 'EDITADO') && (
            <button
              onClick={() => { setContextMenu(null); setValorVendaModal(contextMenu.lote); setValorVendaInput(''); }}
              className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-gray-700 hover:bg-green-50 hover:text-green-700 transition-colors"
            >
              <DollarSign size={14} />
              Definir valor de venda
            </button>
          )}
        </div>
      )}

      {/* Ficha do Leiloeiro Modal */}
      {fichaLeiloeiroLote && (
        <FichaLeiloeiroIndividual
          lote={fichaLeiloeiroLote}
          onClose={() => setFichaLeiloeiroLote(null)}
        />
      )}

      {/* Modal Exportar Vendas */}
      {exportModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
              <h2 className="text-lg font-semibold text-gray-900">Exportar Vendas</h2>
              <button onClick={() => setExportModal(false)} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                <X size={18} />
              </button>
            </div>
            <div className="px-5 py-4 space-y-4">
              <p className="text-sm text-gray-500">
                Gera uma planilha Excel com todos os lotes (dados completos da venda) e as parcelas/pagamentos.
              </p>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Leilão</label>
                <select
                  value={exportLeilaoId}
                  onChange={e => setExportLeilaoId(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">Todos os leilões</option>
                  {leiloes.map(l => (
                    <option key={l.id} value={l.id}>{l.nome}</option>
                  ))}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Data início</label>
                  <input
                    type="date" value={exportDataInicio}
                    onChange={e => setExportDataInicio(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Data fim</label>
                  <input
                    type="date" value={exportDataFim}
                    onChange={e => setExportDataFim(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>
              <p className="text-xs text-gray-400">As datas filtram pela data do leilão. Deixe em branco pra trazer tudo.</p>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Formato</label>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setExportFormato('xlsx')}
                    className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium border transition-colors ${exportFormato === 'xlsx' ? 'bg-blue-600 border-blue-600 text-white' : 'border-gray-300 text-gray-600 hover:bg-gray-50'}`}
                  >
                    Excel (.xlsx)
                  </button>
                  <button
                    type="button"
                    onClick={() => setExportFormato('csv')}
                    className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium border transition-colors ${exportFormato === 'csv' ? 'bg-blue-600 border-blue-600 text-white' : 'border-gray-300 text-gray-600 hover:bg-gray-50'}`}
                  >
                    CSV (.zip)
                  </button>
                </div>
                {exportFormato === 'csv' && (
                  <p className="text-xs text-gray-400 mt-1">Baixa um .zip com dois arquivos: lotes.csv e pagamentos.csv.</p>
                )}
              </div>
            </div>
            <div className="flex gap-3 px-5 py-4 border-t border-gray-100">
              <button
                onClick={() => setExportModal(false)}
                className="flex-1 px-4 py-2 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50"
              >
                Cancelar
              </button>
              <button
                onClick={handleExportar}
                disabled={exportando}
                className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium disabled:opacity-50 flex items-center justify-center gap-2"
              >
                <Download size={16} />
                {exportando ? 'Gerando...' : 'Exportar'}
              </button>
            </div>
          </div>
        </div>
      )}





      {/* Modal Definir Valor de Venda */}
      {valorVendaModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
            <div className="flex items-center justify-between p-5 border-b border-gray-100">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Definir Valor de Venda</h2>
                <p className="text-sm text-gray-500 mt-0.5">Lote {valorVendaModal.numero_lote} — {valorVendaModal.qtd} {valorVendaModal.qtd === 1 ? 'animal' : 'animais'}</p>
              </div>
              <button onClick={() => { setValorVendaModal(null); setValorVendaExcecaoPorCabeca(false); }} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                <X size={20} className="text-gray-500" />
              </button>
            </div>
            <div className="p-5 space-y-4">
              {/* Toggle exceção */}
              {leiloes.find(l => l.id.toString() === selectedLeilao)?.regra_faturamento === 'por_parcela' && (
                <label className="flex items-center gap-3 p-3 bg-amber-50 border border-amber-200 rounded-lg cursor-pointer">
                  <input
                    type="checkbox"
                    checked={valorVendaExcecaoPorCabeca}
                    onChange={(e) => setValorVendaExcecaoPorCabeca(e.target.checked)}
                    className="h-4 w-4 text-amber-600 focus:ring-amber-500 border-gray-300 rounded"
                  />
                  <span className="text-sm text-amber-800 font-medium">Exceção: faturar por cabeça neste lote</span>
                </label>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  {(leiloes.find(l => l.id.toString() === selectedLeilao)?.regra_faturamento === 'por_parcela' && !valorVendaExcecaoPorCabeca)
                    ? 'Valor por Parcela (R$)' 
                    : 'Valor por Cabeça (R$)'}
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={valorVendaInput}
                  onChange={(e) => setValorVendaInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && valorVendaInput && parseFloat(valorVendaInput) > 0) {
                      const lote = valorVendaModal;
                      const leilaoAtual = leiloes.find(l => l.id.toString() === selectedLeilao);
                      const isPorParcela = leilaoAtual?.regra_faturamento === 'por_parcela' && !valorVendaExcecaoPorCabeca;
                      const valorUnit = parseFloat(valorVendaInput);
                      let valorTotalPrecalculado = valorUnit * (lote.qtd || 1);
                      if (isPorParcela && leilaoAtual?.meio_pagamento_padrao_id) {
                        const meio = meiosPagamento.find(m => m.id === leilaoAtual.meio_pagamento_padrao_id);
                        const qtdParcelas = meio?.grupos_parcelas?.reduce((acc, g) => acc + g.quantidade * (g.peso || 1), 0) || 1;
                        valorTotalPrecalculado = valorUnit * qtdParcelas * (lote.qtd || 1);
                      }
                      setValorVendaModal(null);
                      setValorVendaExcecaoPorCabeca(false);
                      navigate(rotaEditarLote(lote.id), {
                        state: { step: 4, valorVendaUnidade: valorUnit, valorTotalPrecalculado, excecaoPorCabeca: valorVendaExcecaoPorCabeca }
                      });
                    }
                  }}
                  placeholder="0,00"
                  className="w-full border border-gray-300 rounded-xl px-4 py-3 text-lg focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-transparent"
                  autoFocus
                />
              </div>
              {valorVendaInput && parseFloat(valorVendaInput) > 0 && (() => {
                const leilaoAtual = leiloes.find(l => l.id.toString() === selectedLeilao);
                const isPorParcela = leilaoAtual?.regra_faturamento === 'por_parcela' && !valorVendaExcecaoPorCabeca;
                const valorUnit = parseFloat(valorVendaInput);
                const qtd = valorVendaModal?.qtd || 1;

                let qtdParcelas = 1;
                let valorTotal = valorUnit * qtd;
                if (isPorParcela && leilaoAtual?.meio_pagamento_padrao_id) {
                  const meio = meiosPagamento.find(m => m.id === leilaoAtual.meio_pagamento_padrao_id);
                  qtdParcelas = meio?.grupos_parcelas?.reduce((acc, g) => acc + g.quantidade * (g.peso || 1), 0) || 1;
                  valorTotal = valorUnit * qtdParcelas * qtd;
                }

                return (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 space-y-1">
                    <div className="flex justify-between text-sm text-gray-600">
                      <span>Quantidade</span>
                      <span className="font-medium">{qtd} {qtd === 1 ? 'animal' : 'animais'}</span>
                    </div>
                    <div className="flex justify-between text-sm text-gray-600">
                      <span>{isPorParcela ? 'Valor por Parcela' : 'Valor unitário'}</span>
                      <span className="font-medium">R$ {valorUnit.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</span>
                    </div>
                    {isPorParcela && (
                      <div className="flex justify-between text-sm text-gray-600">
                        <span>Unidades (c/ peso)</span>
                        <span className="font-medium">{qtdParcelas}x</span>
                      </div>
                    )}
                    <div className="flex justify-between text-base font-semibold text-yellow-800 border-t border-yellow-200 pt-2 mt-1">
                      <span>Valor Total</span>
                      <span>R$ {valorTotal.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</span>
                    </div>
                  </div>
                );
              })()}
            </div>
            <div className="p-5 pt-0 space-y-2">
              <div className="flex gap-3">
                <button
                  onClick={() => { setValorVendaModal(null); setValorVendaExcecaoPorCabeca(false); }}
                  className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition-colors font-medium"
                >
                  Cancelar
                </button>
                <button
                  disabled={!valorVendaInput || parseFloat(valorVendaInput) <= 0 || salvandoValorVenda}
                  onClick={handleSalvarValorVenda}
                  title="Salva o valor e marca o lote como EDITADO, sem comprador. Depois é só completar o comprador e faturar."
                  className="flex-1 px-4 py-3 bg-green-600 hover:bg-green-700 disabled:bg-gray-200 disabled:cursor-not-allowed text-white rounded-xl transition-colors font-medium flex items-center justify-center gap-2"
                >
                  {salvandoValorVenda ? 'Salvando...' : 'Salvar Valor de Venda'}
                </button>
              </div>
              <button
                disabled={!valorVendaInput || parseFloat(valorVendaInput) <= 0}
                onClick={() => {
                  const lote = valorVendaModal;
                  const leilaoAtual = leiloes.find(l => l.id.toString() === selectedLeilao);
                  const isPorParcela = leilaoAtual?.regra_faturamento === 'por_parcela' && !valorVendaExcecaoPorCabeca;
                  const valorUnit = parseFloat(valorVendaInput);
                  let valorTotalPrecalculado = valorUnit * (lote.qtd || 1);
                  if (isPorParcela && leilaoAtual?.meio_pagamento_padrao_id) {
                    const meio = meiosPagamento.find(m => m.id === leilaoAtual.meio_pagamento_padrao_id);
                    const qtdParcelas = meio?.grupos_parcelas?.reduce((acc, g) => acc + g.quantidade * (g.peso || 1), 0) || 1;
                    valorTotalPrecalculado = valorUnit * qtdParcelas * (lote.qtd || 1);
                  }
                  setValorVendaModal(null);
                  setValorVendaExcecaoPorCabeca(false);
                  navigate(rotaEditarLote(lote.id), {
                    state: { step: 4, valorVendaUnidade: valorUnit, valorTotalPrecalculado, excecaoPorCabeca: valorVendaExcecaoPorCabeca }
                  });
                }}
                className="w-full px-4 py-3 bg-yellow-500 hover:bg-yellow-600 disabled:bg-gray-200 disabled:cursor-not-allowed text-white rounded-xl transition-colors font-medium flex items-center justify-center gap-2"
              >
                Ir para Financeiro
                <ArrowRight size={18} />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Confirmação Exclusão de Lote */}
      {showConfirmDeleteLote && loteToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4 shadow-2xl">
            <div className="flex items-center space-x-3 mb-4">
              <div className="flex-shrink-0 p-2 bg-red-100 rounded-lg">
                <Trash2 className="w-6 h-6 text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">
                Excluir Lote
              </h3>
            </div>
            
            <p className="text-gray-600 mb-6">
              Tem certeza que deseja excluir o lote <strong>{loteToDelete.numero_lote}</strong>? Esta ação não pode ser desfeita.
            </p>
            
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowConfirmDeleteLote(false);
                  setLoteToDelete(null);
                }}
                className="px-4 py-2.5 bg-gray-100 text-gray-700 rounded-xl font-medium hover:bg-gray-200 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={confirmarExclusaoLote}
                className="px-4 py-2.5 bg-red-600 text-white rounded-xl font-medium hover:bg-red-700 transition-colors"
              >
                Excluir
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast Messages */}
      <div className="fixed top-4 right-4 z-50 space-y-2">
        {toast.toasts.map((t) => (
          <div
            key={t.id}
            className={`px-6 py-4 rounded-xl shadow-xl text-white font-medium ${
              t.type === 'success' ? 'bg-green-500' :
              t.type === 'error' ? 'bg-red-500' :
              t.type === 'warning' ? 'bg-yellow-500' :
              'bg-blue-500'
            }`}
          >
            {t.message}
          </div>
        ))}
      </div>
    </div>
  );
};

export default LotesLista;